import { useState, useEffect } from 'react';
import { X, Phone, Mail, User, MessageSquare, Send, CheckCircle2 } from 'lucide-react';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ContactFormData {
  fullName: string;
  email: string;
  phone: string;
  message: string;
}

function ContactForm({ 
  onClose, 
  onSuccess 
}: { 
  onClose: () => void; 
  onSuccess: () => void; 
}) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = t('contact.form.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.emailInvalid');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('contact.form.phoneRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact.form.messageRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('contact.form.validationError'));
      return;
    }

    setIsLoading(true);
    try {
      // Transform form data to API format
      const contactData = {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone || undefined,
        subject: 'Contact Form Submission',
        message: formData.message
      };

      console.log('Submitting contact form:', contactData);
      
      const result = await apiService.submitContact(contactData);
      console.log('Contact form submitted successfully:', result);
      
      toast.success(t('contact.form.success'));
      onSuccess();
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(t('contact.form.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Card className="w-full max-w-lg mx-4 border-[#f91bdc]/20 shadow-xl">
      <CardHeader className="relative pb-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onClose}
          className="absolute right-4 top-4 h-8 w-8 p-0 hover:bg-gray-100"
        >
          <X size={18} />
        </Button>
        <div className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
            {t('contact.title')}
          </CardTitle>
          <p className="text-gray-600">
            {t('contact.subtitle')}
          </p>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName" className="flex items-center space-x-2">
              <User size={16} className="text-[#f91bdc]" />
              <span>{t('contact.form.firstName')}</span>
            </Label>
            <Input
              id="fullName"
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              placeholder={t('contact.form.namePlaceholder')}
              className={`transition-all duration-200 ${errors.fullName ? 'border-red-500' : 'focus:border-[#f91bdc]'}`}
            />
            {errors.fullName && (
              <p className="text-sm text-red-500">{errors.fullName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center space-x-2">
              <Mail size={16} className="text-[#f91bdc]" />
              <span>{t('contact.form.email')}</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder={t('contact.form.emailPlaceholder')}
              className={`transition-all duration-200 ${errors.email ? 'border-red-500' : 'focus:border-[#f91bdc]'}`}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center space-x-2">
              <Phone size={16} className="text-[#f91bdc]" />
              <span>{t('contact.form.phone')}</span>
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder={t('contact.form.phonePlaceholder')}
              className={`transition-all duration-200 ${errors.phone ? 'border-red-500' : 'focus:border-[#f91bdc]'}`}
            />
            {errors.phone && (
              <p className="text-sm text-red-500">{errors.phone}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="flex items-center space-x-2">
              <MessageSquare size={16} className="text-[#f91bdc]" />
              <span>{t('contact.form.message')}</span>
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder={t('contact.form.messagePlaceholder')}
              rows={4}
              className={`transition-all duration-200 resize-none ${errors.message ? 'border-red-500' : 'focus:border-[#f91bdc]'}`}
            />
            {errors.message && (
              <p className="text-sm text-red-500">{errors.message}</p>
            )}
          </div>

          <Button 
            type="submit" 
            disabled={isLoading}
            className="w-full bg-[#f91bdc] hover:bg-[#e015c9] text-white py-3 text-base font-medium transition-all duration-200"
          >
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t('contact.form.sending')}</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Send size={18} />
                <span>{t('contact.form.submit')}</span>
              </div>
            )}
          </Button>
        </form>

        <div className="border-t pt-4">
          <div className="text-center text-sm text-gray-600 space-y-2">
            <p>{t('contact.modal.directContact')}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4">
              <a 
                href="tel:+14378871015" 
                className="flex items-center justify-center space-x-2 text-[#f91bdc] hover:text-[#e015c9] transition-colors duration-200"
              >
                <Phone size={14} />
                <span>+1 (437) 887-1015</span>
              </a>
              <a 
                href="mailto:Contact@sayaara.com" 
                className="flex items-center justify-center space-x-2 text-[#f91bdc] hover:text-[#e015c9] transition-colors duration-200"
              >
                <Mail size={14} />
                <span>Contact@sayaara.com</span>
              </a>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ContactSuccess({ onClose }: { onClose: () => void }) {
  const { t } = useLanguage();
  return (
    <Card className="w-full max-w-md mx-4 border-[#f91bdc]/20 shadow-xl">
      <CardHeader className="relative pb-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onClose}
          className="absolute right-4 top-4 h-8 w-8 p-0 hover:bg-gray-100"
        >
          <X size={18} />
        </Button>
      </CardHeader>
      
      <CardContent className="text-center space-y-6 pb-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 size={40} className="text-green-600" />
        </div>
        
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('contact.success.title')}
          </h2>
          <p className="text-gray-600 leading-relaxed">
            {t('contact.success.description')}
          </p>
        </div>

        <div className="bg-[#f91bdc]/5 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            {t('contact.success.urgentText')}{' '}
            <a 
              href="tel:+14378871015" 
              className="text-[#f91bdc] hover:text-[#e015c9] font-medium"
            >
              +1 (437) 887-1015
            </a>
          </p>
        </div>

        <Button 
          onClick={onClose}
          className="w-full bg-[#f91bdc] hover:bg-[#e015c9] text-white"
        >
          {t('contact.success.close')}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setTimeout(() => setShowSuccess(false), 300);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleSuccess = () => {
    setShowSuccess(true);
  };

  const handleClose = () => {
    setShowSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleClose}
      />
      
      <div className="relative z-10 w-full flex justify-center transition-all duration-300 transform scale-100">
        {showSuccess ? (
          <ContactSuccess onClose={handleClose} />
        ) : (
          <ContactForm onClose={handleClose} onSuccess={handleSuccess} />
        )}
      </div>
    </div>
  );
}